/**
 * PTO v3 - permissioned ERC-3643 token created through PTOOrchestrator.createPTO.
 *
 * The BE returns the params of each on-chain step (create-contract,
 * create-pool-distribution, fund-vault); the admin wallet builds, signs and
 * submits every transaction itself, exactly like v1/v2.
 * See note-fe-pto-v3-full-flow.md and PTO_ORCHESTRATOR.md of meyreal-contracts.
 */

/** Token Symbol textbox: any character, at most 10 */
export const PTO_V3_SYMBOL_MAX_LENGTH = 10

/** USDC decimals - pricePerPTO on-chain is in USDC units */
export const PTO_V3_PRICE_DECIMALS = 6

/** sellingStatus of a v3 PTO (same values the BE uses for every version) */
export enum PtoV3SellingStatusEnum {
  OFF_SALE = 1,
  ON_SALE = 2,
  SOLD_OUT = 3,
}

export const PTO_V3_SELLING_STATUS_CONFIG: Record<
  PtoV3SellingStatusEnum,
  { label: string; color: string }
> = {
  [PtoV3SellingStatusEnum.OFF_SALE]: { label: 'Off Sale', color: 'default' },
  [PtoV3SellingStatusEnum.ON_SALE]: { label: 'On Sale', color: 'processing' },
  [PtoV3SellingStatusEnum.SOLD_OUT]: { label: 'Sold Out', color: 'success' },
}

export const PTO_V3_CONTRACT_NAME_MAX_LENGTH = 50

export const PTO_V3_DISPLAY_TOOLTIP =
  'Choose to display PTO on user website or not. When the switch is ON, show the PTO. When the switch is OFF, hide the PTO.'

/** Timelines template - upcoming and fcfs are required, fcfs has no end time */
export const PTO_V3_PHASE_ITEMS: {
  name: string
  required?: boolean
  enabled?: boolean
  isPurchasePhase: boolean
  isEndTimeUnlimited: boolean
}[] = [
  {
    name: 'upcoming',
    required: true,
    enabled: true,
    isPurchasePhase: false,
    isEndTimeUnlimited: false,
  },
  { name: 'whitelist', isPurchasePhase: true, isEndTimeUnlimited: false },
  { name: 'calculating', isPurchasePhase: false, isEndTimeUnlimited: false },
  { name: 'fcfs', required: true, enabled: true, isPurchasePhase: true, isEndTimeUnlimited: true },
  { name: 'end', isPurchasePhase: false, isEndTimeUnlimited: false },
]

/** Friendly texts for the `data.error` codes of GET /admin/pto/:id/create-pool-distribution */
export const PTO_V3_POOL_ERROR_MESSAGES: Record<string, string> = {
  PTO_ORCHESTRATOR_NOT_AGENT:
    'This PTO was created on the previous contract stack and cannot open a pool. Please create a new PTO.',
  PTO_SUPPLY_EXHAUSTED: 'The whole supply of this PTO has been minted, no new pool can be opened.',
  PTO_POOL_STILL_OPEN: 'This PTO still has an open pool. End it before creating a new one.',
  PTO_OWNER_INVALID:
    'The PTO owner does not meet the requirements (a Safe multisig with threshold >= 2 on production).',
}

/** How long to wait for the BE hook to flip the PTO to ON_SALE after createDistributionPool */
export const PTO_V3_ON_SALE_POLL_INTERVAL_MS = 5_000
export const PTO_V3_ON_SALE_POLL_ATTEMPTS = 12

/**
 * Friendly texts for contract revert names the admin flows can hit
 * (PTOOrchestrator.createPTO / createDistributionPool, PTODistribution.endPool).
 * Keys are matched as substrings of the decoded error (PTO_DISTRIBUTION_CHANGES.md).
 */
export const PTO_V3_CONTRACT_ERROR_MESSAGES: Record<string, string> = {
  AgentRole_UnauthorizedAgent: 'The connected wallet is not an agent of the contract.',
  Dist_InvalidPrice: 'The pool price must be greater than 0.',
  Dist_InvalidFeeBasisPoints: 'The transaction fee must be below 100%.',
  Dist_ExceedsMaxPTOSupply: 'The pool amount exceeds the remaining supply of the PTO.',
  Dist_InvalidPoolId: 'This pool does not exist on the current PTODistribution.',
  Dist_PoolEnded: 'This pool has already been ended.',
  EnforcedPause: 'The PTODistribution contract is paused. Contact the contract team.',
  IdFactory_SaltAlreadyTaken:
    'The salt of this PTO is already used on-chain. Ask the BE to issue a new one.',
}

/**
 * Expected Return Calculator (ERC) - percent fields typed as text with a comma as
 * decimal separator: digits + one comma, max 5 chars, max 2 decimals (e.g. "12,5").
 * Sent to the API as numbers (12.5).
 */
export const ERC_PERCENT_MAX_LENGTH = 5
export const ERC_PERCENT_DECIMALS = 2
